import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Account } from '@/domain/accounts/account'
import {
  AccountReportView,
  PERIOD_LABELS,
  ReportPeriodType,
  reportViewFormSchema,
  ReportViewFormValues,
} from '@/domain/reports/report'
import { useCreateReportView, useUpdateReportView } from '@/hooks/api/useReports'
import { zodResolver } from '@hookform/resolvers/zod'
import { Check, Loader2 } from 'lucide-react'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'

interface ReportViewDialogProps {
  isOpen: boolean
  onClose: () => void
  viewToEdit: AccountReportView | null
  mainAccounts: Account[]
  selectedAccountIds: string[]
  onSaved?: (view: AccountReportView) => void
}

export const ReportViewDialog = ({
  isOpen,
  onClose,
  viewToEdit,
  mainAccounts,
  selectedAccountIds,
  onSaved,
}: ReportViewDialogProps) => {
  const createMutation = useCreateReportView()
  const updateMutation = useUpdateReportView()

  const form = useForm<ReportViewFormValues>({
    resolver: zodResolver(reportViewFormSchema),
    defaultValues: {
      name: '',
      description: '',
      accountIds: [],
      periodType: ReportPeriodType.LAST_30_DAYS,
    },
  })

  useEffect(() => {
    if (isOpen) {
      if (viewToEdit) {
        form.reset({
          name: viewToEdit.name,
          description: viewToEdit.description || '',
          accountIds: viewToEdit.accountIds || [],
          periodType: viewToEdit.periodType || ReportPeriodType.LAST_30_DAYS,
        })
      } else {
        form.reset({
          name: '',
          description: '',
          accountIds: selectedAccountIds.length > 0 ? selectedAccountIds : [],
          periodType: ReportPeriodType.LAST_30_DAYS,
        })
      }
    }
  }, [isOpen, viewToEdit, selectedAccountIds, form])

  const isSubmitting = createMutation.isPending || updateMutation.isPending

  const onSubmit = (values: ReportViewFormValues) => {
    if (viewToEdit) {
      updateMutation.mutate(
        {
          id: viewToEdit.id,
          payload: {
            name: values.name,
            description: values.description || null,
            accountIds: values.accountIds,
            periodType: values.periodType,
          },
        },
        {
          onSuccess: (updated) => {
            onSaved?.(updated)
            onClose()
          },
        }
      )
    } else {
      createMutation.mutate(
        {
          name: values.name,
          description: values.description || null,
          accountIds: values.accountIds,
          periodType: values.periodType,
        },
        {
          onSuccess: (created) => {
            onSaved?.(created)
            onClose()
          },
        }
      )
    }
  }

  const toggleAccount = (accountId: string) => {
    const current = form.getValues('accountIds') || []
    if (current.includes(accountId)) {
      form.setValue(
        'accountIds',
        current.filter((id) => id !== accountId),
        { shouldValidate: true }
      )
    } else {
      form.setValue('accountIds', [...current, accountId], { shouldValidate: true })
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {viewToEdit ? 'Editar Vista de Reporte' : 'Crear Vista de Reporte'}
          </DialogTitle>
          <DialogDescription>
            Configurá las cuentas principales a comparar y el rango de tiempo relativo.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nombre de la vista</FormLabel>
                  <FormControl>
                    <Input placeholder="Ej. Comparativa Cuentas de Ingresos" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descripción (Opcional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Breve nota sobre lo que compara esta vista..."
                      rows={2}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="periodType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Comparación temporal relativa</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar período relativo" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.values(ReportPeriodType).map((period) => (
                        <SelectItem key={period} value={period}>
                          {PERIOD_LABELS[period]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="accountIds"
              render={() => (
                <FormItem>
                  <FormLabel>Cuentas seleccionadas para comparar</FormLabel>
                  <div className="border rounded-md p-2 max-h-48 overflow-y-auto space-y-1">
                    {mainAccounts.length === 0 ? (
                      <p className="text-xs text-muted-foreground p-2">
                        No hay cuentas principales disponibles
                      </p>
                    ) : (
                      mainAccounts.map((acc) => {
                        const isChecked = form.watch('accountIds')?.includes(acc.id)
                        return (
                          <div
                            key={acc.id}
                            onClick={() => toggleAccount(acc.id)}
                            className={`flex items-center justify-between p-2 rounded cursor-pointer text-xs transition-colors ${
                              isChecked
                                ? 'bg-primary/10 text-primary font-medium'
                                : 'hover:bg-muted text-foreground'
                            }`}
                          >
                            <span className="font-mono font-semibold">{acc.accountNumber}</span>
                            <span className="truncate max-w-[200px] text-muted-foreground">
                              {acc.accountAlias || `Cuenta ${acc.accountNumber}`}
                            </span>
                            <div
                              className={`w-4 h-4 rounded border flex items-center justify-center ${
                                isChecked
                                  ? 'bg-primary border-primary text-primary-foreground'
                                  : 'border-muted-foreground/40'
                              }`}
                            >
                              {isChecked && <Check className="w-3 h-3" />}
                            </div>
                          </div>
                        )
                      })
                    )}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {viewToEdit ? 'Guardar Cambios' : 'Crear Vista'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

